import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  createFeatureDefinition,
  updateFeatureDefinition,
  getService,
  type FeatureDefinition,
  type FeatureDefinitionCreate,
  type FeatureDefinitionUpdate,
} from '@/lib/api'

interface FeatureEditDrawerProps {
  serviceId: string
  feature: FeatureDefinition | null
  open: boolean
  onOpenChange: (open: boolean) => void
  isCreateMode: boolean
  prefilledGroup?: { code: string; label: string } | null
  existingGroups: Array<{ code: string; label: string }>
}

export function FeatureEditDrawer({
  serviceId,
  feature,
  open,
  onOpenChange,
  isCreateMode,
  prefilledGroup,
  existingGroups,
}: FeatureEditDrawerProps) {
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = React.useState(false)
  const [isNewGroup, setIsNewGroup] = React.useState(false)

  // Form state
  const [code, setCode] = React.useState('')
  const [groupCode, setGroupCode] = React.useState('')
  const [groupLabelKey, setGroupLabelKey] = React.useState('')
  const [featureLabelKey, setFeatureLabelKey] = React.useState('')
  const [featureDescriptionKey, setFeatureDescriptionKey] = React.useState('')
  const [sortOrder, setSortOrder] = React.useState(0)
  const [isHighlight, setIsHighlight] = React.useState(false)

  // Load service to get plans for feature matrix
  const { data: service } = useQuery({
    queryKey: ['service', serviceId],
    queryFn: () => getService(serviceId),
    enabled: open,
  })

  // Initialize form when feature/mode changes
  React.useEffect(() => {
    if (open) {
      if (isCreateMode) {
        setCode('')
        setFeatureLabelKey('')
        setFeatureDescriptionKey('')
        setSortOrder(0)
        setIsHighlight(false)

        if (prefilledGroup) {
          setGroupCode(prefilledGroup.code)
          setGroupLabelKey(prefilledGroup.label)
          setIsNewGroup(false)
        } else {
          setGroupCode('')
          setGroupLabelKey('')
          setIsNewGroup(existingGroups.length === 0)
        }
      } else if (feature) {
        setCode(feature.code)
        setGroupCode(feature.group_code)
        setGroupLabelKey(feature.group_label_key)
        setFeatureLabelKey(feature.feature_label_key)
        setFeatureDescriptionKey(feature.feature_description_key || '')
        setSortOrder(feature.sort_order)
        setIsHighlight(feature.is_highlight)
        setIsNewGroup(false)
      }
    }
  }, [open, isCreateMode, feature, prefilledGroup, existingGroups.length])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: FeatureDefinitionCreate) => createFeatureDefinition(serviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-definitions', serviceId] })
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: FeatureDefinitionUpdate) =>
      updateFeatureDefinition(serviceId, feature!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-definitions', serviceId] })
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] })
    },
  })

  const handleGroupSelect = (selectedCode: string) => {
    if (selectedCode === '__new__') {
      setIsNewGroup(true)
      setGroupCode('')
      setGroupLabelKey('')
    } else {
      setIsNewGroup(false)
      const group = existingGroups.find(g => g.code === selectedCode)
      if (group) {
        setGroupCode(group.code)
        setGroupLabelKey(group.label)
      }
    }
  }

  const handleSave = async () => {
    if (!code.trim() || !groupCode.trim() || !groupLabelKey.trim() || !featureLabelKey.trim()) {
      return
    }

    setIsSaving(true)
    try {
      const data = {
        code: code.trim(),
        group_code: groupCode.trim(),
        group_label_key: groupLabelKey.trim(),
        feature_label_key: featureLabelKey.trim(),
        feature_description_key: featureDescriptionKey.trim() || null,
        sort_order: sortOrder,
        is_highlight: isHighlight,
      }

      if (isCreateMode) {
        await createMutation.mutateAsync(data)
      } else {
        await updateMutation.mutateAsync(data)
      }

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save feature:', error)
    } finally {
      setIsSaving(false)
    }
  }

  // Get feature status in each plan
  const planFeatureMatrix = React.useMemo(() => {
    if (!service?.plans || !code) return []
    return service.plans.map(plan => ({
      code: plan.code,
      name: plan.name,
      enabled: plan.features[code] === true,
    }))
  }, [service?.plans, code])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle>
            {isCreateMode ? 'Add Feature' : `Edit: ${feature?.code}`}
          </SheetTitle>
          <SheetDescription>
            {isCreateMode
              ? 'Define a new feature that can be toggled per plan'
              : 'Update feature definition'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-4">
          {/* Code */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Code <span className="text-destructive">*</span>
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="team_battles"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Matches the key in plan.features JSONB
            </p>
          </div>

          {/* Group Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Group <span className="text-destructive">*</span>
            </label>
            {existingGroups.length > 0 && !isNewGroup ? (
              <div className="space-y-2">
                <select
                  value={groupCode}
                  onChange={(e) => handleGroupSelect(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Select group...</option>
                  {existingGroups.map(g => (
                    <option key={g.code} value={g.code}>{g.code}</option>
                  ))}
                  <option value="__new__">+ Create new group</option>
                </select>
                {groupCode && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Label key:</span>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {groupLabelKey}
                    </code>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Input
                  value={groupCode}
                  onChange={(e) => setGroupCode(e.target.value)}
                  placeholder="gamification"
                  className="font-mono"
                />
                <div className="space-y-1">
                  <label className="text-sm text-muted-foreground">Group Label Key</label>
                  <Input
                    value={groupLabelKey}
                    onChange={(e) => setGroupLabelKey(e.target.value)}
                    placeholder="feature_group.gamification"
                  />
                </div>
                {existingGroups.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsNewGroup(false)}
                  >
                    Use existing group
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Feature Label Key */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Feature Label Key <span className="text-destructive">*</span>
            </label>
            <Input
              value={featureLabelKey}
              onChange={(e) => setFeatureLabelKey(e.target.value)}
              placeholder="feature.team_battles"
            />
            <p className="text-xs text-muted-foreground">
              i18n key for the feature name displayed in UI
            </p>
          </div>

          {/* Feature Description Key */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description Key</label>
            <Input
              value={featureDescriptionKey}
              onChange={(e) => setFeatureDescriptionKey(e.target.value)}
              placeholder="feature_desc.team_battles"
            />
            <p className="text-xs text-muted-foreground">
              Optional i18n key for feature description
            </p>
          </div>

          {/* Sort Order */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Sort Order</label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="w-24"
            />
          </div>

          {/* Highlight */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="highlight"
              checked={isHighlight}
              onChange={(e) => setIsHighlight(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <label htmlFor="highlight" className="text-sm font-medium cursor-pointer">
              Highlight Feature
            </label>
            <p className="text-xs text-muted-foreground">
              Show prominently in pricing comparison
            </p>
          </div>

          {/* Plan Feature Matrix (only for existing features) */}
          {!isCreateMode && planFeatureMatrix.length > 0 && (
            <div className="space-y-2 pt-4 border-t">
              <label className="text-sm font-medium">Plan Feature Matrix</label>
              <p className="text-xs text-muted-foreground mb-3">
                Which plans have this feature enabled (read-only, edit in Plan settings)
              </p>
              <div className="rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Plan</th>
                      <th className="px-3 py-2 text-center font-medium w-24">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planFeatureMatrix.map(plan => (
                      <tr key={plan.code} className="border-b last:border-b-0">
                        <td className="px-3 py-2">{plan.name}</td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant={plan.enabled ? 'default' : 'secondary'}>
                            {plan.enabled ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="mt-6 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !code.trim() || !groupCode.trim() || !groupLabelKey.trim() || !featureLabelKey.trim()}
          >
            {isSaving ? 'Saving...' : isCreateMode ? 'Create Feature' : 'Save Changes'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
